import React, { type ReactNode } from 'react';

import { Button } from '../Button';
import { Select } from '../Select';
import { useUIColors } from '../../theme';
import { cx, type Styleable } from '../../variants';

/**
 * EAA-1821: the "count · prev · page · next" footer under a hand-built table.
 *
 * Every copy of this in the console rendered its disabled prev/next as a dimmed
 * button that was still clickable — which fires a request for page 0 and then
 * renders an empty table. Here `disabled` is BOTH: the real attribute (so it
 * cannot fire) and the visual treatment (so it cannot look clickable).
 *
 * All copy arrives as nodes, already formatted by the caller: this package
 * holds no domain vocabulary and cannot know whether a page counter should read
 * "Trang 2/9" or "2 of 9".
 */

export interface TablePagerLabels {
    prev: ReactNode;
    next: ReactNode;
    /** Already-formatted page counter, e.g. "Trang 2/9". */
    page: ReactNode;
    /** Accessible label for the first-page button. Required only with `onFirst`. */
    first?: string;
    /** Accessible label for the last-page button. Required only with `onLast`. */
    last?: string;
    /**
     * EAA-1839: accessible names for prev/next, needed when `prev`/`next` are
     * glyphs ("‹" / "›") rather than words — a screen reader cannot read those.
     * Omitted, the visible content is the accessible name, as before.
     */
    prevAria?: string;
    nextAria?: string;
}

export interface TablePagerProps extends Styleable {
    page: number;
    totalPages: number;
    onPrev: () => void;
    onNext: () => void;
    labels: TablePagerLabels;
    /** Leading readout, e.g. "Hiển thị 20 / 218 bản ghi". */
    summary?: ReactNode;
    /**
     * EAA-1804: `AccountsPagination` and `RoleMapPagination` (apps/portal) both
     * hand-rolled a first/prev/numbered-window/next/last footer on top of this
     * component because it only modeled prev/current/next. Passing `pages` (and
     * `onFirst`/`onLast`) renders that shape here instead of a third hand-built
     * copy.
     *
     * The WINDOW ITSELF is not computed in this component — deliberately. How
     * many page numbers to show around the current one, whether to always pin
     * page 1 and the last page, and whether to collapse a gap into an ellipsis
     * are product decisions, and the two existing callers already disagree
     * (`AccountsPagination`/`RoleMapPagination` both use current±2 plus first/
     * last today, but nothing says a third caller wants the same window). The
     * caller computes `pages` and hands it over as plain numbers.
     */
    pages?: number[];
    /** Called with a page number from `pages`. Required when `pages` is passed. */
    onGoTo?: (page: number) => void;
    /** Jump to page 1. Omit to render no first-page button (the default). */
    onFirst?: () => void;
    /** Jump to `totalPages`. Omit to render no last-page button (the default). */
    onLast?: () => void;
    /**
     * EAA-1839: rows-per-page picker, rendered between the summary and the page
     * buttons. Omit it (the default) and nothing is rendered, so every existing
     * caller's markup is unchanged. `label` is a node because this package holds
     * no copy — the caller passes "Số dòng" or "Rows per page".
     */
    pageSize?: {
        value: number;
        options: number[];
        onChange: (size: number) => void;
        label?: ReactNode;
    };
}

export function TablePager({
    page,
    totalPages,
    onPrev,
    onNext,
    labels,
    summary,
    pages,
    onGoTo,
    onFirst,
    onLast,
    pageSize,
    className,
    style,
}: TablePagerProps): React.ReactElement {
    const colors = useUIColors();

    const buttonStyle: React.CSSProperties = {
        height: 32,
        padding: '0 12px',
        borderRadius: 6,
        fontSize: 13,
        fontWeight: 500,
    };

    return (
        <div
            className={cx('ui-table-pager', className)}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: 16,
                color: colors.textSecondary,
                fontSize: 13,
                ...style,
            }}
        >
            {summary && <span className="ui-table-pager__summary">{summary}</span>}
            <span className="ui-table-pager__spacer" style={{ flex: '1 1 auto' }} />
            {pageSize && (
                <label
                    className="ui-table-pager__page-size"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}
                >
                    {pageSize.label}
                    <Select
                        size="small"
                        value={pageSize.value}
                        onChange={(value) => pageSize.onChange(Number(value))}
                        options={pageSize.options.map((size) => ({
                            value: size,
                            label: String(size),
                        }))}
                        // EAA-1839: a width FLOOR, not a fixed width. antd sizes a
                        // `Select` to its current value and the popup inherits that
                        // width, so with a 2-character value selected the control
                        // measured 43px and the 3-character option "100" rendered
                        // ellipsised to "1…" — a picker whose choices are unreadable.
                        // 80px clears the widest option plus the selector padding and
                        // the arrow; a floor so wider caller options still grow it.
                        style={{ minWidth: 80 }}
                    />
                </label>
            )}
            {onFirst && (
                <Button
                    className="ui-table-pager__first"
                    disabled={page <= 1}
                    onClick={onFirst}
                    aria-label={labels.first}
                    style={buttonStyle}
                >
                    «
                </Button>
            )}
            <Button
                className="ui-table-pager__prev"
                disabled={page <= 1}
                onClick={onPrev}
                aria-label={labels.prevAria}
                style={buttonStyle}
            >
                {labels.prev}
            </Button>
            {pages && pages.length > 0 ? (
                <span className="ui-table-pager__pages" style={{ display: 'flex', gap: 4 }}>
                    {pages.map((p) => (
                        <Button
                            key={p}
                            className="ui-table-pager__page-number"
                            type={p === page ? 'primary' : undefined}
                            onClick={() => onGoTo?.(p)}
                            aria-current={p === page ? 'page' : undefined}
                            style={{ ...buttonStyle, minWidth: 32, padding: '0 8px' }}
                        >
                            {p}
                        </Button>
                    ))}
                </span>
            ) : (
                <span
                    className="ui-table-pager__page"
                    style={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}
                >
                    {labels.page}
                </span>
            )}
            <Button
                className="ui-table-pager__next"
                disabled={page >= totalPages}
                onClick={onNext}
                aria-label={labels.nextAria}
                style={buttonStyle}
            >
                {labels.next}
            </Button>
            {onLast && (
                <Button
                    className="ui-table-pager__last"
                    disabled={page >= totalPages}
                    onClick={onLast}
                    aria-label={labels.last}
                    style={buttonStyle}
                >
                    »
                </Button>
            )}
        </div>
    );
}

export default TablePager;
